import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, prettify } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PortalUpload } from "@/components/PortalUpload";
import { FileText, FileImage, Download, File as FileIcon } from "lucide-react";
import { toast } from "sonner";

const iconFor = (ct) => (ct?.startsWith("image/") ? FileImage : ct?.includes("pdf") ? FileText : FileIcon);

export default function PortalDocuments() {
  const { user } = useAuth();
  const isPartner = user?.role === "partner_vendor";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/portal/documents"); setDocs(data.items || []); }
    catch { toast.error("Could not load documents"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (d) => {
    try {
      const res = await api.get(`/portal/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h2 className="font-display text-2xl font-bold text-[#252221]">Documents</h2>
        <PortalUpload endpoint="/portal/documents/upload" label="Upload document" testid="portal-documents-upload" onUploaded={load} />
      </div>
      <p className="text-sm text-muted-foreground mb-5">{isPartner ? "Files shared with you by the MovingMyMama team and files you upload." : "Files shared with you by your MovingMyMama team — and anything you upload for us."}</p>
      {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><Skeleton className="h-16" /><Skeleton className="h-16" /></div> :
        docs.length === 0 ? <Card className="p-10 text-center text-muted-foreground border-[#E6EDF2]" data-testid="portal-documents-empty">No documents yet. Use “Upload document” to share a file.</Card> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {docs.map((d) => {
              const I = iconFor(d.content_type);
              return (
                <div key={d.id} className="flex items-center gap-3 rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="portal-document-card">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><I size={20} className="text-[#1C4358]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate text-[#252221]">{d.original_filename}</div>
                    <div className="text-[11px] text-muted-foreground">{d.category ? prettify(d.category) + " · " : ""}{formatDate(d.created_at)}</div>
                  </div>
                  <button onClick={() => open(d)} className="p-2 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358] transition-colors" data-testid="portal-document-download" aria-label="Download"><Download size={16} /></button>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
